import { useEffect, useRef, useState } from 'react';
import type { ChatEntry } from '../../../shared/types';
import { useMe } from '../store/gameStore';

interface ChatProps {
  entries: ChatEntry[];
  onSend: (text: string) => void;
}

export default function Chat({ entries, onSend }: ChatProps) {
  const me = useMe();
  const [text, setText] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries.length]);

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText('');
  };

  const messages = entries.filter((e) => e.authorId !== null);

  return (
    <div className="lud-glass flex h-56 flex-col overflow-hidden rounded-3xl">
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-lud-muted">
        <span className="h-2 w-2 rounded-full bg-[#4aa8ff]" />
        Chat
      </div>
      <div ref={listRef} className="lud-scroll flex-1 space-y-1.5 overflow-y-auto px-4 py-3">
        {messages.length === 0 ? (
          <p className="text-xs italic text-lud-muted/60">No messages yet — say hi!</p>
        ) : (
          messages.map((e) => (
            <p key={e.id} className="text-xs leading-snug text-lud-muted">
              <span className="font-bold text-lud-ink">{e.authorName}:</span> {e.text}
              {e.authorId === me?.id && <span className="text-lud-muted/50"> (you)</span>}
            </p>
          ))
        )}
      </div>
      <div className="flex items-center gap-2 border-t border-white/10 p-2.5">
        <input
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 200))}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="Say something…"
          className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.07] px-3 py-2 text-sm text-lud-ink outline-none transition placeholder:text-lud-muted/50 focus:border-lud-accent/60 focus:ring-2 focus:ring-lud-accent/20"
        />
        <button
          onClick={submit}
          disabled={!text.trim()}
          className="rounded-xl bg-gradient-to-br from-[#ffd257] to-[#ff8a3d] px-3.5 py-2 text-sm font-bold text-white transition hover:brightness-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-35"
        >
          Send
        </button>
      </div>
    </div>
  );
}
